"use client";

import { LogOut, User, Settings, Key } from "lucide-react";
import { COMMON } from "@/lib/test/test-ids";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/components/providers/UserProvider";
import { ROUTES } from "@/app/index";
import { useTranslation } from "react-i18next";

export default function UserDropdown() {
  const { user, profile, logout } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    // Use our enhanced logout method
    await logout();
    // Replace history to prevent back button issues
    navigate('/auth/login', { replace: true });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="relative h-10 w-10 rounded-full"
          data-testid={COMMON.NAVBAR.USER_DROPDOWN}
        >
          <Avatar className="h-10 w-10">
            <AvatarFallback>{getInitials(profile?.name || user?.email)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-56" 
        align="end" 
        forceMount
        data-testid={COMMON.NAVBAR.USER_DROPDOWN_MENU.CONTENT}
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{profile?.name || user?.email}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
            <p className="text-xs leading-none text-muted-foreground mt-1">
              {profile?.role || 'User'}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => navigate(ROUTES.VIEWS.PROFILE)}
            data-testid={COMMON.NAVBAR.USER_DROPDOWN_MENU.PROFILE_LINK}
          >
            <User className="mr-2 h-4 w-4" />
            <span>{t('user.profile', 'Profile')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate(ROUTES.VIEWS.PIN_MANAGEMENT)}
            data-testid={COMMON.NAVBAR.USER_DROPDOWN_MENU.PAYMENT_PIN_LINK}
          >
            <Key className="mr-2 h-4 w-4" />
            <span>{t('user.payment_pin', 'Payment PIN')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate(ROUTES.VIEWS.SETTINGS)}
            data-testid={COMMON.NAVBAR.USER_DROPDOWN_MENU.SETTINGS_LINK}
          >
            <Settings className="mr-2 h-4 w-4" />
            <span>{t('user.settings', 'Settings')}</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          data-testid={COMMON.NAVBAR.USER_DROPDOWN_MENU.LOGOUT_BUTTON}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t('user.logout', 'Log out')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
